import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { AlertTriangle, ArrowRight, CheckCircle, Loader2, Search, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/formatters';
import InlineNewDealDialog from './InlineNewDealDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RefinanceWizard = ({ 
  open, 
  onOpenChange, 
  getAuthHeaders, 
  currentDealId,
  currentDealNumber,
  mode = 'from_old', // 'from_old' = selecting new deal, 'from_new' = selecting old deals
  onSuccess 
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [availableDeals, setAvailableDeals] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOldDeals, setSelectedOldDeals] = useState([]);
  const [selectedNewDeal, setSelectedNewDeal] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [payoffOverrides, setPayoffOverrides] = useState({});
  const [applying, setApplying] = useState(false);
  const [notes, setNotes] = useState('');
  // Inline "Create New Deal" sub-dialog state. Only relevant in `from_old`
  // mode — the operator is searching for a target NEW deal and may decide
  // to spin one up without leaving the refi flow.
  const [newDealDialogOpen, setNewDealDialogOpen] = useState(false);
  const [currentDealMerchantId, setCurrentDealMerchantId] = useState(null);
  const [sourceDealForPrefill, setSourceDealForPrefill] = useState(null);

  // Pull the current deal's merchant_contact_id when the wizard opens — used
  // to pre-select the same merchant on the inline new-deal form. Operator can
  // override but the default is the obvious thing. Light fetch, non-blocking.
  useEffect(() => {
    if (!open || !currentDealId || mode !== 'from_old') return;
    let cancelled = false;
    axios.get(`${API}/deals/${currentDealNumber || currentDealId}`, { headers: getAuthHeaders() })
      .then(res => {
        if (!cancelled) {
          setCurrentDealMerchantId(res.data?.company_id || res.data?.merchant_contact_id || null);
          setSourceDealForPrefill(res.data || null);
        }
      })
      .catch(() => { /* prefill is best-effort; failure leaves field blank */ });
    return () => { cancelled = true; };
  }, [open, currentDealId, currentDealNumber, mode, getAuthHeaders]);

  // Called by InlineNewDealDialog after a successful POST /api/deals. The new
  // deal lands here ready to be auto-selected as the refi target — operator
  // continues the wizard with zero extra clicks.
  const handleNewDealCreated = useCallback(async (created) => {
    // Normalize to the shape the picker uses (matches `availableDeals`).
    const normalized = {
      id: created.id,
      deal_id: created.deal_id,
      deal_name: created.deal_name,
      deal_shortcode: created.deal_shortcode,
      status: created.status,
      balance: created.balance,
      balance_with_fees: created.balance_with_fees,
    };
    setAvailableDeals(prev => [normalized, ...prev]);
    setSelectedNewDeal(normalized);
    // Half-done-refi fix (Josh 07/28, #10798→#10887): don't stop at
    // "selected" — run the payoff preview immediately and land on the
    // CONFIRM step so the only remaining click is Apply (which posts the
    // REFINANCE_PAYOFF, links both deals, and closes out the old one).
    try {
      const response = await axios.post(`${API}/refinance/preview`, {
        new_deal_id: created.id,
        old_deal_ids: [currentDealId]
      }, { headers: getAuthHeaders() });
      setPreviewData(response.data);
      const overrides = {};
      response.data.old_deals?.forEach(od => { overrides[od.deal_id] = od.suggested_payoff; });
      setPayoffOverrides(overrides);
      setStep(2);
      toast.success(`Deal #${created.deal_id} created — review the payoff and click Apply to FINISH the refinance`);
    } catch (error) {
      toast.error('New deal created, but the payoff preview failed — click Preview to continue the refinance manually');
    }
  }, [currentDealId, getAuthHeaders]);

  const fetchAvailableDeals = useCallback(async () => {
    setLoading(true);
    try {
      // mode === 'from_old' → user is on the old deal selecting the NEW target
      //   → fetch with purpose=new so already-refied/paid-off deals (e.g. links
      //     in a longer chain) remain selectable.
      // mode === 'from_new' → user is on the new deal selecting OLD sources
      //   → keep the default purpose=old filter.
      const purpose = mode === 'from_old' ? 'new' : 'old';
      const response = await axios.get(`${API}/refinance/available-deals?tenant=live&purpose=${purpose}`, {
        headers: getAuthHeaders()
      });
      setAvailableDeals(response.data.deals || []);
    } catch (error) {
      toast.error('Failed to load available deals');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedOldDeals([]);
      setSelectedNewDeal(null);
      setPreviewData(null);
      setPayoffOverrides({});
      setNotes('');
      fetchAvailableDeals();
    }
  }, [open, fetchAvailableDeals]);

  const filteredDeals = availableDeals.filter(d => {
    const term = searchTerm.toLowerCase();
    return (
      d.deal_id?.toString().toLowerCase().includes(term) ||
      d.deal_name?.toLowerCase().includes(term) ||
      d.deal_shortcode?.toLowerCase().includes(term)
    );
  }).filter(d => d.id !== currentDealId);

  const handleSelectOldDeal = (deal) => {
    if (selectedOldDeals.find(d => d.id === deal.id)) {
      setSelectedOldDeals(selectedOldDeals.filter(d => d.id !== deal.id));
    } else {
      setSelectedOldDeals([...selectedOldDeals, deal]);
    }
  };

  const handleSelectNewDeal = (deal) => {
    setSelectedNewDeal(deal);
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      let newDealId, oldDealIds;
      
      if (mode === 'from_old') {
        // Current deal is old, selectedNewDeal is the new deal
        newDealId = selectedNewDeal.id;
        oldDealIds = [currentDealId];
      } else {
        // Current deal is new, selectedOldDeals are the old deals
        newDealId = currentDealId;
        oldDealIds = selectedOldDeals.map(d => d.id);
      }

      const response = await axios.post(`${API}/refinance/preview`, {
        new_deal_id: newDealId,
        old_deal_ids: oldDealIds
      }, { headers: getAuthHeaders() });

      setPreviewData(response.data);
      
      // Initialize overrides with suggested amounts
      const overrides = {};
      response.data.old_deals?.forEach(od => {
        overrides[od.deal_id] = od.suggested_payoff;
      });
      setPayoffOverrides(overrides);
      
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const items = previewData.old_deals.map(od => ({
        old_deal_id: od.deal_id,
        payoff_amount: payoffOverrides[od.deal_id] || od.suggested_payoff,
        payoff_date: new Date().toISOString()
      }));

      const response = await axios.post(`${API}/refinance/apply`, {
        new_deal_id: previewData.new_deal.id,
        items,
        notes
      }, { headers: getAuthHeaders() });

      toast.success(response.data.message);
      const rd = response.data;
      if (rd.syndicators_copied > 0) {
        toast.success(`${rd.syndicators_copied} syndicator(s) carried over from the old deal`);
      } else if (rd.needs_syndicator_choice && rd.needs_syndicator_choice.length > 1) {
        const pick = window.prompt(
          `Multiple deals refinanced in. Copy syndicators from which deal?\nEnter one of: ${rd.needs_syndicator_choice.join(', ')} (leave blank for none)`,
          rd.needs_syndicator_choice[0]);
        if (pick && rd.needs_syndicator_choice.map(String).includes(pick.trim())) {
          try {
            const cp = await axios.post(`${API}/refinance/copy-syndicators`,
              { new_deal_id: rd.new_deal.id, source_deal_id: pick.trim() },
              { headers: getAuthHeaders() });
            toast.success(`${cp.data.copied} syndicator(s) copied from #${pick.trim()}`);
          } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to copy syndicators');
          }
        }
      }
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply refinance');
    } finally {
      setApplying(false);
    }
  };

  const handlePayoffChange = (dealId, value) => {
    setPayoffOverrides({
      ...payoffOverrides,
      [dealId]: parseFloat(value) || 0
    });
  };

  const totalPayoff = previewData?.old_deals?.reduce((sum, od) => 
    sum + (payoffOverrides[od.deal_id] || od.suggested_payoff), 0) || 0;

  // Guard: never let the operator silently abandon an un-applied refinance.
  const guardedOpenChange = (nextOpen) => {
    if (!nextOpen && step === 2 && previewData && !applying) {
      if (!window.confirm('The refinance has NOT been applied yet — no payoff transaction or refi link exists. The old deal will NOT be closed out.\n\nClose anyway?')) return;
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={guardedOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="refinance-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRight className="w-5 h-5 text-indigo-600" />
            Refinance Wizard {step === 1 ? '- Select Deals' : '- Confirm Payoff'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'from_old' 
              ? `Refinance Deal #${currentDealNumber} into a new deal`
              : `Add old deals to refinance into Deal #${currentDealNumber}`
            }
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Select Deals */}
        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="flex gap-2 items-stretch">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by deal ID, name, or shortcode..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="deal-search-input"
                />
              </div>
              {mode === 'from_old' && (
                <Button
                  variant="outline"
                  onClick={() => setNewDealDialogOpen(true)}
                  className="shrink-0"
                  data-testid="create-new-deal-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Create New Deal
                </Button>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              </div>
            ) : (
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {filteredDeals.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No deals found</p>
                ) : (
                  filteredDeals.slice(0, 50).map(deal => {
                    const isSelected = mode === 'from_old' 
                      ? selectedNewDeal?.id === deal.id
                      : selectedOldDeals.find(d => d.id === deal.id);
                    
                    return (
                      <div
                        key={deal.id}
                        onClick={() => mode === 'from_old' ? handleSelectNewDeal(deal) : handleSelectOldDeal(deal)}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-indigo-500 bg-indigo-50' 
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        data-testid={`deal-option-${deal.deal_id}`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-3">
                            {mode === 'from_new' && (
                              <Checkbox checked={!!isSelected} />
                            )}
                            <div>
                              <span className="font-medium">#{deal.deal_id}</span>
                              <span className="text-gray-500 ml-2">{deal.deal_name || deal.deal_shortcode}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <Badge variant="outline">{deal.status}</Badge>
                            <span className="text-sm font-medium">{formatCurrency(deal.balance_with_fees || deal.balance)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Selected deals summary */}
            {mode === 'from_new' && selectedOldDeals.length > 0 && (
              <Card className="bg-indigo-50 border-indigo-200">
                <CardContent className="py-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-indigo-800">
                      {selectedOldDeals.length} deal(s) selected
                    </span>
                    <span className="text-sm text-indigo-600">
                      Total Effective Balance: {formatCurrency(selectedOldDeals.reduce((sum, d) => sum + (d.balance_with_fees || d.balance || 0), 0))}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {mode === 'from_old' && selectedNewDeal && (
              <Card className="bg-indigo-50 border-indigo-200">
                <CardContent className="py-3">
                  <div className="text-sm text-indigo-800">
                    <span className="font-medium">New Deal Selected: </span>
                    #{selectedNewDeal.deal_id} - {selectedNewDeal.deal_name || selectedNewDeal.deal_shortcode}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 2: Preview & Confirm */}
        {step === 2 && previewData && (
          <div className="space-y-4 py-4">
            {/* New Deal Info */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-800">New Deal</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-600">Deal #:</span> {previewData.new_deal.deal_id_number}</div>
                  <div><span className="text-gray-600">Funding:</span> {formatCurrency(previewData.new_deal.funding_amount)}</div>
                  <div><span className="text-gray-600">Payback:</span> {formatCurrency(previewData.new_deal.payback)}</div>
                  <div><span className="text-gray-600">Status:</span> {previewData.new_deal.status}</div>
                </div>
              </CardContent>
            </Card>

            {/* Old Deals with Payoff Amounts */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-700">Old Deals to Refinance</h4>
              {previewData.old_deals.map((od, idx) => (
                <Card key={od.deal_id} className={od.is_already_refinanced ? 'border-amber-300 bg-amber-50' : ''}>
                  <CardContent className="py-3">
                    {od.is_already_refinanced && (
                      <div className="flex items-center gap-2 text-amber-700 text-sm mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        {od.refi_warning}
                      </div>
                    )}
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">#{od.deal_id_number} - {od.deal_name}</div>
                        <div className="text-sm text-gray-600 mt-1">
                          <div>Balance Due: {formatCurrency(od.remaining_payback)}</div>
                          <div>Fees: {formatCurrency(od.total_fees || 0)}</div>
                          <div className="font-medium text-indigo-700">Effective Balance: {formatCurrency(od.effective_balance || od.suggested_payoff)}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <label className="text-xs text-gray-500 block mb-1">Payoff Amount</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={payoffOverrides[od.deal_id] || ''}
                          onChange={(e) => handlePayoffChange(od.deal_id, e.target.value)}
                          className="w-32 text-right"
                          data-testid={`payoff-input-${od.deal_id_number}`}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Total */}
            <Card className="bg-indigo-100 border-indigo-300">
              <CardContent className="py-3">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-indigo-900">Total Payoff</span>
                  <span className="text-lg font-bold text-indigo-700">{formatCurrency(totalPayoff)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Notes (optional)</label>
              <Input
                placeholder="Add notes about this refinance..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="refinance-notes-input"
              />
            </div>

            {/* Warnings */}
            {previewData.warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="text-amber-800 font-medium mb-1">Warnings</div>
                {previewData.warnings.map((w, idx) => (
                  <div key={idx} className="text-sm text-amber-700">{w.warning}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handlePreview}
                disabled={mode === 'from_old' ? !selectedNewDeal : selectedOldDeals.length === 0}
                data-testid="preview-refinance-btn"
              >
                Preview Refinance
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button 
                onClick={handleApply}
                disabled={applying}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="apply-refinance-btn"
              >
                {applying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Applying...
                  </>
                ) : (
                  'Apply Refinance'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Inline "Create New Deal" sub-dialog. Mounted as a sibling to the main
          DialogContent so it stacks on top correctly. Only opened from
          from_old mode where the operator is picking the target new deal. */}
      <InlineNewDealDialog
        open={newDealDialogOpen}
        onOpenChange={setNewDealDialogOpen}
        getAuthHeaders={getAuthHeaders}
        prefillMerchantId={currentDealMerchantId}
        prefillSourceDeal={sourceDealForPrefill}
        onCreated={handleNewDealCreated}
      />
    </Dialog>
  );
};

export default RefinanceWizard;
